import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FieldError, FormProvider, useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";

import Button from "components/Button";
import DataTable from "components/DataTable";
import Input from "components/Input";
import Select from "components/Select";
import Toggle from "components/Toggle";
import {
  DUPLICATED_INVOICE_TABLE_HEADER_LIST,
  FR_CONTRACT_TYPE,
  PERCENTAGE_LIST
} from "constants/haulpay-broker";
import { CARRIER_PAYMENT_SPEED } from "constants/haulpay-carrier";
import { routes } from "constants/routes";
import FRLineItems from "features/main/HaulPay/modules/Broker/FRLineItems";
import CarrierSelector from "features/main/HaulPay/Shared/CarrierSelector";
import { frPermission } from "helpers/convertFormData";
import { formatNumber } from "helpers/formatNumber";
import useDeviceType from "hooks/useDeviceType";
import { useUserFeature } from "hooks/useUserFeature";
import {
  IAccount,
  ICompany,
  IFundingRequestForm,
  IInvoiceData,
  ILineList,
  IPaymentProfile
} from "models/HaulPay";
import { searchPoNumberAPI } from "services/api/haulpay";
import { selectAccount } from "store/auth/selector";
import { selectFactoringProfile } from "store/user/selector";
import DuplicatedInvoiceForMobile from "./DuplicatedInvoiceForMobile";
import FrResult from "./FrResult";
import { QUERY_AUTO_GENERATED_LOAD_NUMBER } from "./query";
import styles from "./style.module.scss";
import PaymentSpeedInput from "components/PaymentSpeedInput";
import { useBrokerContext } from "context/BrokerContext";
import { fundingRequestFormSchema } from "./fundingRequestFormSchema";
import DebtorSearch from "components/DebtorSearchInput";
import { FUNDING_REQUEST_CONTRACT_TYPES } from "helpers/constantTypes";
import DollarInput from "components/DollarInput";
import { useAnalytics } from "hooks/useAnalytics";
import { IOption } from "../../../../../../models/Form";

const PaymentWarningMessages = ({
  isNonFactoredStandard,
  isNonFactoredBillOut
}: Readonly<{
  isNonFactoredStandard: boolean;
  isNonFactoredBillOut: boolean;
}>) => (
  <p className="mobile:mt-3">
    {isNonFactoredStandard ? (
      <span className="text-warning">
        Warning: The carrier will be paid when you are paid when the shipper settles the invoice in
        the future. Do not pay the carrier from your side with this option selected.
      </span>
    ) : null}

    {isNonFactoredBillOut ? (
      <span className="text-warning">
        You will be paid when the shipper pays their invoice. We will not pay the carrier with this
        option.
      </span>
    ) : null}
  </p>
);

interface ISharedValue {
  companyValue?: ICompany;
  setStepCallback: (step: number) => void;
  fundingRequestFormData?: IFundingRequestForm;
  selectedPaymentProfile?: IPaymentProfile;
  setIsShowSearch: (show: boolean) => void;
  setSelectedPaymentProfile: (item: IPaymentProfile) => void;
  setFormDataCallback: (data: IFundingRequestForm) => void;
  createFundingRequest: (frInfo: IFundingRequestForm, lineItems: ILineList[]) => Promise<void>;
}

export default function FundingRequestInfo({
  setStepCallback,
  fundingRequestFormData,
  selectedPaymentProfile,
  setSelectedPaymentProfile,
  setFormDataCallback,
  createFundingRequest
}: Readonly<ISharedValue>) {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { isDesktop } = useDeviceType();
  const { isAllowLineItems } = useUserFeature();
  const { trackAllEvents } = useAnalytics();
  const [frType, setFrType] = useState(fundingRequestFormData?.contractType);
  const { userPermission } = useSelector(selectAccount);
  const factoringProfile = useSelector(selectFactoringProfile);
  const [poNumber, setPoNumber] = useState<string>();
  const [poNumberError, setPoNumberError] = useState<FieldError>();
  const [duplicatedInvoice, setDuplicatedInvoice] = useState<IInvoiceData[]>([]);
  const [isShowDuplicatedInvoice, setIsShowDuplicatedInvoice] = useState(false);

  const selfFinanceFrLimit = factoringProfile?.self_finance_fr_limit / 100 ?? 0;
  const allowSelfFinanceFr = factoringProfile?.allow_self_finance_fr ?? false;
  const selfFinanceFrRemainingBalance = factoringProfile?.self_finance_fr_remaining_balance ?? 0;
  const allowNegativeMargin = factoringProfile?.allow_negative_margin ?? false;
  const negativeMarginValue = factoringProfile?.negative_margin_value / 100 ?? 0;
  const negativeMarginLimit = factoringProfile?.negative_margin_limit ?? 0;

  const [isShowResult, setIsShowResult] = useState(false);
  const validationSchema = useMemo(
    () =>
      fundingRequestFormSchema(
        factoringProfile?.enable_hide_debtor,
        selfFinanceFrLimit,
        allowSelfFinanceFr,
        selfFinanceFrRemainingBalance,
        factoringProfile?.allow_fr_without_carrier,
        allowNegativeMargin,
        negativeMarginValue,
        negativeMarginLimit
      ),
    [factoringProfile]
  );

  const methods = useForm<Partial<IFundingRequestForm>>({
    resolver: yupResolver(validationSchema)
  });
  const isNonFactoredStandard = frType?.value === FR_CONTRACT_TYPE[1].value;
  const isNonFactoredBillOut = frType?.value === FR_CONTRACT_TYPE[2].value;
  const isSelfFinanceNoInvoice = frType?.value === FR_CONTRACT_TYPE[3].value;
  const frTypeOptions = FR_CONTRACT_TYPE.filter(
    (request) => userPermission.available_contract_types[request.value]
  );

  const { data: autoGeneratedLoadNumber } = useQuery(QUERY_AUTO_GENERATED_LOAD_NUMBER, {
    skip: factoringProfile?.auto_generate_load_numbers && state?.userLoadNumber,
    fetchPolicy: "cache-and-network"
  });

  const {
    register,
    control,
    getValues,
    setValue,
    reset,
    clearErrors,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = methods;
  const amount = useWatch({ control: control, name: "amount" });
  const contractType = useWatch({ control: control, name: "contractType" });
  const paymentProfileAmount = useWatch({ control: control, name: "paymentProfileAmount" });
  const debtor = useWatch({ control: control, name: "debtor" });
  const carrier = useWatch({ control: control, name: "identifyNumber" });
  const payoutDays = useWatch({ control: control, name: "payoutDays" });
  const factoringFeeSplitRate = useWatch({ control: control, name: "factoringFeeSplitRate" });
  const isMarginAdvance = useWatch({ control: control, name: "marginAdvance" });

  const fundingRequestValue = fundingRequestFormData?.contractType.value;
  const { frCalculation, calculateFR } = useBrokerContext();
  const onChangeLineItemAmount = (amount: number) => {
    setValue("amount", amount);
  };

  const setPaymentProfile = (item: IPaymentProfile) => {
    setSelectedPaymentProfile(item);
    setValue("identifyNumber", item?.company_name);
  };

  const mapAccountsToOptions = useCallback(
    (
      accounts: IAccount[]
    ): Array<IOption & { method?: string; accountNumber?: string; type?: string }> => {
      const filtered = accounts.filter(
        ({ method }) => selectedPaymentProfile?.enable_pay_via_check || method !== "CHECK"
      );
      return filtered.map(({ id, method, accountNumberRedacted }, idx) => {
        if (
          selectedPaymentProfile?.factoring_company?.name &&
          selectedPaymentProfile.factoring_company.accountNumber === accountNumberRedacted
        ) {
          return {
            id: idx,
            value: id,
            label: `${selectedPaymentProfile.factoring_company.name} (**** ${selectedPaymentProfile.factoring_company.accountNumber})`,
            type: "FACTORING",
            method,
            accountNumber: accountNumberRedacted
          };
        }
        return {
          id: idx,
          value: id,
          label: method === "CHECK" ? "Pay Via Check" : `${method} (**** ${accountNumberRedacted})`,
          type: method,
          method,
          accountNumber: accountNumberRedacted
        };
      });
    },
    [selectedPaymentProfile?.id]
  );

  const paymentProfileAccountsOptions = useMemo(
    () =>
      (selectedPaymentProfile?.accounts?.length ?? 0) > 0
        ? mapAccountsToOptions(selectedPaymentProfile!.accounts!)
        : [
            {
              id: 0,
              value: "0",
              label: "ACH"
            }
          ],
    [selectedPaymentProfile?.id]
  );

  const onSubmit = async (data: IFundingRequestForm) => {
    const frLineList = data.lineItems.map((item) => ({
      amount: item.amount ? Number(item.amount * 100) : 0,
      category: item.category.value
    })) as ILineList[];
    data.paymentProfilePaymentMethod = paymentProfileAccountsOptions.find(
      (account) => account.id === (data.paymentProfilePaymentMethod as IOption).id
    )?.method;
    setFormDataCallback(data);
    await createFundingRequest(data, frLineList);
    trackAllEvents("create_funding_request_post");
  };

  const goToInvoice = (invoice: IInvoiceData) => {
    navigate(routes.HAULPAY_BROKER_MANAGE_PAY, {
      state: {
        debtor: invoice.debtor?.id,
        debtorName: invoice.debtor?.dba
      }
    });
  };

  useEffect(() => {
    reset({
      ...fundingRequestFormData,
      lineItems: fundingRequestFormData?.lineItems,
      paymentProfilePaymentMethod: {
        id: 0,
        value: "0",
        label: "ACH"
      },
      payoutDays: fundingRequestFormData?.payoutDays,
      factoringFeeSplitRate: fundingRequestFormData?.factoringFeeSplitRate,
      ...(state
        ? {
            contractType: FR_CONTRACT_TYPE[0],
            userLoadNumber: fundingRequestFormData?.userLoadNumber ?? state?.userLoadNumber
          }
        : {
            contractType: fundingRequestFormData?.contractType
          })
    });
  }, []);

  useEffect(() => {
    if (selectedPaymentProfile && Object.keys(selectedPaymentProfile).length > 0) {
      if (selectedPaymentProfile.factoring_company?.name) {
        setValue(
          "paymentProfilePaymentMethod",
          paymentProfileAccountsOptions.find((account) => account.type === "FACTORING")
        );
        setValue("factoringFeeSplitRate", PERCENTAGE_LIST[2]);
      }
      if ((selectedPaymentProfile.accounts ?? []).length) {
        if (selectedPaymentProfile.enable_pay_via_check) {
          setValue(
            "paymentProfilePaymentMethod",
            paymentProfileAccountsOptions.find((account) => account.method === "CHECK")
          );
        } else {
          setValue("paymentProfilePaymentMethod", paymentProfileAccountsOptions[0]);
        }
        setValue(
          "factoringFeeSplitRate",
          PERCENTAGE_LIST.find(
            ({ amount }) => amount === Number(selectedPaymentProfile.factoring_fee_split_rate)
          )
        );
      } else {
        setValue("paymentProfilePaymentMethod", paymentProfileAccountsOptions[0]);
        setValue(
          "factoringFeeSplitRate",
          PERCENTAGE_LIST.find(
            ({ amount }) => amount === Number(selectedPaymentProfile.factoring_fee_split_rate)
          )
        );
      }
      setValue(
        "payoutDays",
        CARRIER_PAYMENT_SPEED.find((item) => item.value === selectedPaymentProfile.payout_days)
      );
    } else {
      setValue("paymentProfilePaymentMethod", "ACH");
    }
  }, [selectedPaymentProfile?.id]);

  useEffect(() => {
    async function calculateFRAPI() {
      if (
        amount &&
        paymentProfileAmount &&
        (debtor ||
          [FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE].includes(
            getValues("contractType.value")
          )) &&
        ((carrier && payoutDays) ||
          [
            FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT,
            FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD
          ].includes(getValues("contractType.value")))
      ) {
        const paymentSpeed = getValues("payoutDays.value");
        try {
          await calculateFR({
            debtor: getValues("debtor.id"),
            factored: !getValues("contractType.value").startsWith("NON_FACTORED"),
            invoice_amount: Number(getValues("amount") ?? 0) * 100,
            apply_reserve_holdback: getValues("marginAdvance"),
            bill_out_only: getValues("contractType.value") === "NON_FACTORED_BILL_OUT",
            contract_type: getValues("contractType.value"),
            create_rates_revision: false,
            factoring_fee_split_rate: Number(factoringFeeSplitRate?.value ?? 0),
            payment_profile: selectedPaymentProfile?.id,
            payment_profile_amount: 0,
            payment_profile_invoice_amount: Number(getValues("paymentProfileAmount") ?? 0) * 100,
            payment_speed: parseInt(paymentSpeed?.match(/\d+/g)?.[0] ?? "0", 10),
            self_finance_requested: getValues("contractType.value").startsWith("SELF_FINANCE")
          });
          setIsShowResult(true);
        } catch {
          setIsShowResult(false);
        }
      }
    }

    calculateFRAPI();
  }, [
    amount,
    contractType,
    paymentProfileAmount,
    debtor,
    carrier,
    isMarginAdvance,
    payoutDays,
    factoringFeeSplitRate
  ]);

  useEffect(() => {
    if (
      factoringProfile?.enable_reserve_holdback_hybrid &&
      factoringProfile?.default_enable_reserve_holdback
    ) {
      setValue("marginAdvance", true);
    } else {
      setValue("marginAdvance", false);
    }
  }, [factoringProfile?.default_enable_reserve_holdback]);

  useEffect(() => {
    if (!state?.userLoadNumber && autoGeneratedLoadNumber?.autogeneratedLoadNumber) {
      setValue("userLoadNumber", autoGeneratedLoadNumber.autogeneratedLoadNumber);
    }
  }, [autoGeneratedLoadNumber]);

  useEffect(() => {
    const searchPoNumber = async () => {
      if (poNumber) {
        try {
          const response = await searchPoNumberAPI(poNumber);
          if (response?.data?.length) {
            setPoNumberError({
              type: "custom",
              message: "The PO # has been used on another invoice Click Here to view"
            });
            setDuplicatedInvoice(response.data);
          } else {
            setPoNumberError(undefined);
          }
        } catch {
          /* Empty */
        }
      }
    };

    searchPoNumber();
  }, [poNumber]);

  return (
    <FormProvider {...methods}>
      <form className={styles.fundingInfoContainer} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.container}>
          {state ? (
            <>
              <h4 className="text-xl font-medium">Funding Info</h4>

              <Controller
                name="contractType"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={frTypeOptions}
                    value={value}
                    label="&nbsp;*Funding Request Type"
                    onChange={(value) => {
                      onChange(value);
                      setFrType(value);
                    }}
                  />
                )}
              />
            </>
          ) : null}

          <div className="desktop:flex desktop:w-full desktop:gap-4">
            <div className={`${styles.shippingInvoiceAmount} shipper-amount`}>
              <DollarInput
                name="amount"
                label={
                  <div className="pl-1">
                    <span>* Shipper Invoice Amount </span>
                    <span className="italic">(Carriers will not see this)</span>
                  </div>
                }
                error={errors.amount}
                disabled={isAllowLineItems}
              />

              <FRLineItems onChangeLineItemAmount={onChangeLineItemAmount} />
            </div>

            {!isDesktop ? (
              <PaymentWarningMessages
                isNonFactoredBillOut={isNonFactoredBillOut}
                isNonFactoredStandard={isNonFactoredStandard}
              />
            ) : null}

            {!isNonFactoredBillOut ? (
              <DollarInput
                name="paymentProfileAmount"
                label="&nbsp;*Carrier Invoice Amount"
                error={errors?.paymentProfileAmount}
                className="flex-1 carrier-amount"
              />
            ) : null}
          </div>

          {isDesktop ? (
            <PaymentWarningMessages
              isNonFactoredBillOut={isNonFactoredBillOut}
              isNonFactoredStandard={isNonFactoredStandard}
            />
          ) : null}

          <div className="flex gap-3 mobile:flex-col desktop:justify-between desktop:gap-4">
            {!isSelfFinanceNoInvoice ? (
              <Controller
                name="debtor"
                control={control}
                render={({ field, fieldState }) => (
                  <DebtorSearch
                    className="!gap-0.5 desktop:flex-1 debtor-search"
                    name={field.name}
                    value={field.value?.dba}
                    onSelect={(company: ICompany) => {
                      field.onChange(company);
                      clearErrors("debtor");
                    }}
                    label={
                      <div className="pl-1">
                        <span>*Shipper/Debtor </span>
                        <span className="italic">(Carriers will not see this)</span>
                      </div>
                    }
                    error={fieldState.error}
                    disabled={fundingRequestValue === FR_CONTRACT_TYPE[3].value}
                    isConnectedDebtor
                  />
                )}
              />
            ) : null}

            {frPermission(frType).isSelfFinance && !isDesktop ? (
              <p className="text-warning">
                {`You’ve chosen to pay the carrier using your own funds. When self-financing QuickPay
              the limit for each funding request is ${formatNumber(factoringProfile?.self_finance_fr_limit / 100, 2)}. You can Fund ${formatNumber(factoringProfile?.self_finance_fr_remaining_balance / 100, 2)} more worth
              of funding requests.`}
              </p>
            ) : null}

            {!isNonFactoredBillOut ? (
              <div className={`${styles.carrier} carrier-search`}>
                <span className="text-sm text-tertiary">&nbsp;*Carrier</span>
                <CarrierSelector
                  register={register}
                  errors={errors}
                  getValues={getValues}
                  setPaymentProfile={setPaymentProfile}
                  isSearchCompanyName
                  useGroupIC
                />
              </div>
            ) : null}
          </div>

          {frPermission(frType).isSelfFinance && isDesktop ? (
            <p className="text-warning">
              {`You’ve chosen to pay the carrier using your own funds. When self-financing QuickPay
  the limit for each funding request is ${formatNumber(factoringProfile?.self_finance_fr_limit / 100, 2)}. You can Fund ${formatNumber(factoringProfile?.self_finance_fr_remaining_balance / 100, 2)} more worth
  of funding requests.`}
            </p>
          ) : null}

          <div className="flex flex-col gap-4 desktop:grid desktop:grid-cols-3 desktop:gap-5">
            <Input
              name="userLoadNumber"
              register={register}
              label="&nbsp;*Load Number"
              error={errors.userLoadNumber}
              disabled={factoringProfile?.auto_generate_load_numbers}
            />
            <Input name="carrierProNumber" register={register} label="&nbsp; Carrier Pro #" />
            <Input
              name="purchaseOrderNumber"
              register={register}
              label="&nbsp; PO #"
              error={poNumberError}
              isHideError
              onBlur={(event) => {
                setPoNumber(event.target.value);
                setValue("purchaseOrderNumber", event.target.value);
              }}
            />
          </div>

          {poNumberError ? (
            <div className="self-end text-error mobile:text-sm mobile:-mt-2">
              <span>The PO # has been used on another invoice Click Here </span>
              <button
                type="button"
                className="text-info"
                onClick={() => setIsShowDuplicatedInvoice((prev) => !prev)}>
                Click Here
              </button>
              <span> to view</span>
            </div>
          ) : null}

          {isShowDuplicatedInvoice && duplicatedInvoice.length ? (
            <>
              {isDesktop ? (
                <DataTable
                  headers={DUPLICATED_INVOICE_TABLE_HEADER_LIST}
                  data={duplicatedInvoice.map((invoice) => ({
                    ...invoice,
                    linkToInvoice: {
                      action: () => goToInvoice(invoice)
                    }
                  }))}
                />
              ) : (
                duplicatedInvoice.map((invoice) => (
                  <DuplicatedInvoiceForMobile
                    key={invoice.id}
                    data={invoice}
                    goToInvoice={goToInvoice}
                  />
                ))
              )}
            </>
          ) : null}

          <div className="flex gap-3 mobile:flex-col desktop:gap-4">
            <Controller
              name="paymentProfilePaymentMethod"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <Select
                  value={value as IOption}
                  options={paymentProfileAccountsOptions}
                  label={
                    <p>
                      <span className="pl-1">Payment Method</span>
                      <>
                        {selectedPaymentProfile?.factoring_company?.name ? (
                          <span className="pl-1 font-semibold text-error">Factor Will Be Paid</span>
                        ) : null}
                      </>
                    </p>
                  }
                  onChange={onChange}
                  onBlur={onBlur}
                  className="flex-1 capitalize"
                  disabled={Boolean(selectedPaymentProfile?.factoring_company?.name)}
                />
              )}
            />
            {frPermission(frType).isNonFactored ? (
              <>
                <PaymentSpeedInput
                  control={control}
                  name="payoutDays"
                  customOnChange={(paymentSpeed) => {
                    !selectedPaymentProfile?.factoring_company?.name
                      ? setValue(
                          "factoringFeeSplitRate",
                          PERCENTAGE_LIST.find((obj) => obj.value === String(paymentSpeed.splitFee))
                        )
                      : setValue("factoringFeeSplitRate", PERCENTAGE_LIST[0]);
                  }}
                  inputProps={{
                    className: "flex-1 capitalize carrier-payment-speed"
                  }}
                />
                <Controller
                  name="factoringFeeSplitRate"
                  control={control}
                  render={({ field: { value = PERCENTAGE_LIST[0], onChange } }) => (
                    <Select
                      options={PERCENTAGE_LIST}
                      value={value}
                      onChange={onChange}
                      label="&nbsp; Broker's % of QP Fee"
                      direction="up"
                      disabled={selectedPaymentProfile?.factoring_company?.name}
                      className="flex-1"
                    />
                  )}
                />
              </>
            ) : null}
          </div>

          {factoringProfile?.enable_reserve_holdback_hybrid &&
          !contractType?.value.startsWith("NON_FACTORED") ? (
            <Toggle
              className="items-center self-end"
              name="marginAdvance"
              register={register}
              suffix="Create w/Margin Advance"
              disabled={contractType?.value.startsWith("SELF_FINANCE")}
            />
          ) : null}

          {isShowResult && frCalculation && (
            <FrResult frCalculation={frCalculation} isMarginAdvance={isMarginAdvance} />
          )}
        </div>

        <div className={styles.footer}>
          {!state ? (
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => {
                setStepCallback(0);
                setFormDataCallback(getValues() as IFundingRequestForm);
              }}>
              Back
            </Button>
          ) : null}
          <Button type="submit" size="lg" disabled={isSubmitting}>
            {isSubmitting ? <Icon path={mdiLoading} size={2} spin={1} /> : "Submit"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
